import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Button, 
  Box, 
  Stack,
  Switch,
  FormControlLabel 
} from '@mui/material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { LoadingWrapper, SkeletonCard, StatsLoadingOverlay } from '../components/loading';

const LoadingDemoPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [showOverlay, setShowOverlay] = useState(false);

  const mockStats = [
    { title: 'Total Users', value: '1,234', icon: <PeopleAltIcon /> },
    { title: 'Total Invoices', value: '567', icon: <ReceiptIcon /> },
    { title: 'Support Tickets', value: '89', icon: <SupportAgentIcon /> },
    { title: 'Revenue', value: '$45,678', icon: <ReceiptIcon /> },
  ];

  const renderStatCard = (stat, index) => (
    <Grid item xs={12} sm={6} md={3} key={index}>
      <Card>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            {stat.title}
          </Typography>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {stat.icon}
            <Typography variant="h6">{stat.value}</Typography>
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h3" gutterBottom sx={{ 
        background: 'linear-gradient(45deg, #667eea, #764ba2)',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        fontWeight: 'bold',
        textAlign: 'center',
        mb: 4
      }}>
        🎨 Beautiful Loading States Demo
      </Typography>

      {/* Controls */}
      <Card sx={{ mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>Controls</Typography>
          <Stack direction="row" spacing={3} alignItems="center">
            <Button 
              variant="contained" 
              onClick={() => setIsLoading(!isLoading)}
              sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' }
              }}
            >
              {isLoading ? 'Stop Loading' : 'Start Loading'}
            </Button>
            <FormControlLabel
              control={
                <Switch 
                  checked={showOverlay} 
                  onChange={(e) => setShowOverlay(e.target.checked)}
                  sx={{ color: 'white' }}
                />
              }
              label="Show Overlay Demo"
              sx={{ color: 'white' }}
            />
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={4}>
        {/* Skeleton Cards Demo */}
        <Grid item xs={12}>
          <Typography variant="h4" gutterBottom>1. Skeleton Loading Cards</Typography>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom>Default Skeleton</Typography>
              <SkeletonCard variant="stat" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom>Action Skeleton</Typography>
              <SkeletonCard variant="action" />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <Typography variant="h6" gutterBottom>Wide Skeleton</Typography>
              <SkeletonCard variant="wide" />
            </Grid>
          </Grid>
        </Grid>

        {/* LoadingWrapper Demo */}
        <Grid item xs={12}>
          <Typography variant="h4" gutterBottom>2. LoadingWrapper with Skeleton</Typography>
          <LoadingWrapper
            isLoading={isLoading}
            loadingType="skeleton"
            skeletonCount={4}
            gridProps={{ xs: 12, sm: 6, md: 3 }}
            title="Loading statistics..."
            subtitle="Please wait while we fetch your data"
          >
            <Grid container spacing={2}>
              {mockStats.map((stat, index) => renderStatCard(stat, index))}
            </Grid>
          </LoadingWrapper>
        </Grid>

        {/* Overlay Variants Demo */}
        <Grid item xs={12}>
          <Typography variant="h4" gutterBottom sx={{ mt: 4 }}>3. Loading Overlays</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>Default Overlay</Typography>
              <Box sx={{ position: 'relative', height: 200, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <StatsLoadingOverlay 
                  isLoading={showOverlay} 
                  variant="default"
                  title="Loading..."
                  subtitle="Please wait"
                />
                <Box sx={{ p: 3 }}>
                  <Typography>Your content here</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>Minimal Overlay</Typography>
              <Box sx={{ position: 'relative', height: 200, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <StatsLoadingOverlay 
                  isLoading={showOverlay} 
                  variant="minimal"
                  title="Loading stats..."
                />
                <Box sx={{ p: 3 }}>
                  <Typography>Your content here</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>Advanced Overlay</Typography>
              <Box sx={{ position: 'relative', height: 200, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <StatsLoadingOverlay 
                  isLoading={showOverlay} 
                  variant="advanced"
                  title="Processing Data"
                  subtitle="Advanced animations in progress"
                />
                <Box sx={{ p: 3 }}>
                  <Typography>Your content here</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Grid>

        {/* Combined Demo */}
        <Grid item xs={12}>
          <Typography variant="h4" gutterBottom sx={{ mt: 4 }}>4. Combined Loading (Skeleton + Overlay)</Typography>
          <LoadingWrapper
            isLoading={isLoading}
            loadingType="both"
            skeletonCount={3}
            overlayVariant="advanced"
            gridProps={{ xs: 12, sm: 6, md: 4 }}
            title="Syncing Data..."
            subtitle="Advanced processing in progress"
            containerSx={{ minHeight: 300 }}
          >
            <Grid container spacing={2}>
              {mockStats.slice(0, 3).map((stat, index) => renderStatCard(stat, index))}
            </Grid>
          </LoadingWrapper>
        </Grid>
      </Grid>

      {/* Usage Instructions */}
      <Card sx={{ mt: 6, background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)', color: 'white' }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>🚀 How to Use in Your Components</Typography>
          <Typography variant="body1" sx={{ fontFamily: 'monospace', backgroundColor: 'rgba(0,0,0,0.2)', p: 2, borderRadius: 1, mt: 2 }}>
            {`import { LoadingWrapper, SkeletonCard, StatsLoadingOverlay } from '../components/loading';

// For skeleton loading:
<LoadingWrapper isLoading={loading} loadingType="skeleton">
  {/* Your content */}
</LoadingWrapper>

// For overlay loading:
<LoadingWrapper isLoading={loading} loadingType="overlay" overlayVariant="advanced">
  {/* Your content */}
</LoadingWrapper>

// For individual skeleton cards:
<SkeletonCard variant="stat" />
<SkeletonCard variant="action" />
<SkeletonCard variant="wide" />`}
          </Typography>
        </CardContent>
      </Card>
    </Container>
  );
};

export default LoadingDemoPage;
